/* eslint-disable camelcase */

import {ChannelModel} from '../../models/api/channelModel';
import {InboxModel} from '../../models/api/inboxModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {WithClientId} from '../editors/commons/clientIdModel';
import {
  ChannelParam,
  ChannelsParam,
  CommentBodyAndAuthorsParam,
  CustomAttributeBooleanParam,
  CustomAttributeDatetimeParam,
  CustomAttributeNumberParam,
  CustomAttributeStringParam,
  CustomAttributeTeammateParam,
  DateIsParam,
  DaysOfTheWeekParam,
  DelayParam,
  FieldParam,
  InboxesParam,
  InboxParam,
  RecipientComparatorParam,
  TagParam,
  TagsParam,
  TeammateParam,
  TeammatesParam,
  TimeRangeParam,
  UnrepliedAfterParam,
} from '../editors/commons/paramModel';
import {
  CompareExpressionBooleanParam,
  CompareExpressionDateTimeParam,
  CompareExpressionListParam,
  CompareExpressionNumberParam,
  CompareExpressionOtherParam,
  CompareExpressionStringParam,
  CompareExpressionTeammateParam,
} from '../editors/expressions/expressionParamModel';
import {DropdownGroup} from './ruleEditor/dropdowns/multipleGroupsDropdownHelpers';

export enum RuleConditionTypesEnum {
  MESSAGE_BODY = 'body',
  MESSAGE_HTML_BODY = 'html_body',
  MESSAGE_HEADERS = 'headers',
  INBOX_IS = 'inbox_is',
  INBOX_IN = 'inbox_in',
  INBOX_BELONGS_TO = 'inbox_belongs_to',
  ALL_INDIVIDUAL_INBOXES = 'all_individual_inboxes',
  ALL_SHARED_INBOXES = 'all_shared_inboxes',
  CHANNEL_IS = 'channel_is',
  CHANNEL_IN = 'channel_in',
  ADDED_TAG_IS = 'added_tag_is',
  REMOVED_TAG_IS = 'removed_tag_is',
  HAS_TAG = 'has_tag',
  HASNT_TAG = 'hasnt_tag',
  HAS_TAG_IN = 'has_tag_in',
  HASNT_TAGS = 'hasnt_tags',
  HAS_NO_TAGS = 'has_no_tags',
  COMMENT_BODY = 'comment_body',
  COMMENT_BODY_AND_AUTHORS = 'comment_body_and_authors',
  LANGUAGE = 'langage',
  COMMENT_AUTHOR = 'comment_author',
  MENTIONS = 'mentions',
  ASSIGNEE_CHANGED = 'assignee_changed',
  ASSIGNEE_IS = 'assignee_is',
  IS_UNASSIGNED = 'is_unassigned',
  ASSIGNEE_IN = 'assignee_in',
  ASSIGNEE_NOT_IN = 'assignee_not_in',
  IS_EARLIEST_SLA_BREACH = 'is_earliest_sla_breach',
  IS_ARCHIVED = 'is_archived',
  IS_OPEN = 'is_open',
  IS_SNOOZED = 'is_snoozed',
  SUBJECT = 'subject',
  CONTACT_IN_GROUP = 'contact_in_group',
  CONTACT_NOT_IN_GROUP = 'contact_not_in_group',
  DAY_IS = 'day_is',
  HAS_ATTACHMENTS = 'has_attachments',
  HAS_NO_ATTACHMENTS = 'has_no_attachments',
  ATTACHMENT_EXTENSION_IS = 'attachment_extension_is',
  ATTACHMENT_NAME_CONTAINS = 'attachment_name_contains',
  MESSAGE_TYPE = 'message_type',
  MESSAGE_DIRECTION = 'message_direction',
  TIME_IS = 'time_is',
  DATE_IS = 'date_is',
  UNREPLIED_AFTER = 'unreplied_after',
  UNASSIGNED_AFTER = 'unassigned_after',
  DELAY_SINCE_TRIGGER = 'delay_since_trigger',
  MESSAGE_FROM = 'from',
  MESSAGE_TO = 'to',
  MESSAGE_CC = 'cc',
  MESSAGE_BCC = 'bcc',
  MESSAGE_ANY_RECIPIENT = 'any_recipient',
  MESSAGE_TO_CC = 'to_cc',
  DURING_BUSINESS_HOURS = 'during_business_hours',
  OUTSIDE_BUSINESS_HOURS = 'outside_business_hours',
  CUSTOM_ATTRIBUTE_STRING = 'custom_attribute_string',
  CUSTOM_ATTRIBUTE_NUMBER = 'custom_attribute_number',
  CUSTOM_ATTRIBUTE_BOOLEAN = 'custom_attribute_boolean',
  CUSTOM_ATTRIBUTE_DATETIME = 'custom_attribute_datetime',
  CUSTOM_ATTRIBUTE_TEAMMATE = 'custom_attribute_teammate',
  ACCOUNT_CUSTOM_ATTRIBUTE_STRING = 'account_custom_attribute_string',
  ACCOUNT_CUSTOM_ATTRIBUTE_NUMBER = 'account_custom_attribute_number',
  ACCOUNT_CUSTOM_ATTRIBUTE_BOOLEAN = 'account_custom_attribute_boolean',
  ACCOUNT_CUSTOM_ATTRIBUTE_DATETIME = 'account_custom_attribute_datetime',
  ACCOUNT_CUSTOM_ATTRIBUTE_TEAMMATE = 'account_custom_attribute_teammate',
  HAS_CONVERSATION_METADATA = 'has_conversation_metadata',
  HAS_NOT_CONVERSATION_METADATA = 'has_not_conversation_metadata',
  COMPARE_EXPRESSION_BOOLEAN = 'compare_expression_boolean',
  COMPARE_EXPRESSION_NUMBER = 'compare_expression_number',
  COMPARE_EXPRESSION_STRING = 'compare_expression_string',
  COMPARE_EXPRESSION_DATETIME = 'compare_expression_datetime',
  COMPARE_EXPRESSION_TEAMMATE = 'compare_expression_teammate',
  COMPARE_EXPRESSION_LIST = 'compare_expression_list',
  COMPARE_EXPRESSION_OTHER = 'compare_expression_other',
}

// Common condition type data.

export type Condition<T extends RuleConditionTypesEnum, P = {}> = {
  readonly integration_id: number;
  readonly restricted?: ReadonlyArray<string>;
  readonly params: P;
  readonly type: T;
};

// List of all condition types.

export type AllIndividualInboxes = Condition<RuleConditionTypesEnum.ALL_INDIVIDUAL_INBOXES>;

export type AllSharedInboxes = Condition<RuleConditionTypesEnum.ALL_SHARED_INBOXES>;

export type InboxIs = Condition<RuleConditionTypesEnum.INBOX_IS, InboxParam>;

export type InboxIn = Condition<RuleConditionTypesEnum.INBOX_IN, InboxesParam>;

export type InboxBelongsTo = Condition<RuleConditionTypesEnum.INBOX_BELONGS_TO, TeammatesParam>;

export type ChannelIs = Condition<RuleConditionTypesEnum.CHANNEL_IS, ChannelParam>;

export type ChannelIn = Condition<RuleConditionTypesEnum.CHANNEL_IN, ChannelsParam>;

export type AddedTagIs = Condition<RuleConditionTypesEnum.ADDED_TAG_IS, TagParam>;

export type RemovedTagIs = Condition<RuleConditionTypesEnum.REMOVED_TAG_IS, TagParam>;

export type HasTag = Condition<RuleConditionTypesEnum.HAS_TAG, TagParam>;

export type DoesNotHaveTag = Condition<RuleConditionTypesEnum.HASNT_TAG, TagParam>;

export type HasTags = Condition<RuleConditionTypesEnum.HAS_TAG_IN, TagsParam>;

export type HasntTags = Condition<RuleConditionTypesEnum.HASNT_TAGS, TagsParam>;

export type HasNoTags = Condition<RuleConditionTypesEnum.HAS_NO_TAGS>;

export type AssigneeChanged = Condition<RuleConditionTypesEnum.ASSIGNEE_CHANGED, TeammatesParam>;

export type AssigneeIs = Condition<RuleConditionTypesEnum.ASSIGNEE_IS, TeammateParam>;

export type IsUnassigned = Condition<RuleConditionTypesEnum.IS_UNASSIGNED>;

export type AssigneeIn = Condition<RuleConditionTypesEnum.ASSIGNEE_IN, TeammatesParam>;

export type AssigneeNotIn = Condition<RuleConditionTypesEnum.ASSIGNEE_NOT_IN, TeammatesParam>;

export type IsEarliestSlaBreach = Condition<RuleConditionTypesEnum.IS_EARLIEST_SLA_BREACH>;

export type IsArchived = Condition<RuleConditionTypesEnum.IS_ARCHIVED>;

export type IsOpen = Condition<RuleConditionTypesEnum.IS_OPEN>;

export type IsSnoozed = Condition<RuleConditionTypesEnum.IS_SNOOZED>;

export type FromCondition = Condition<RuleConditionTypesEnum.MESSAGE_FROM, RecipientComparatorParam>;

export type ToCondition = Condition<RuleConditionTypesEnum.MESSAGE_TO, RecipientComparatorParam>;

export type CcCondition = Condition<RuleConditionTypesEnum.MESSAGE_CC, RecipientComparatorParam>;

export type ToCcCondition = Condition<RuleConditionTypesEnum.MESSAGE_TO_CC, RecipientComparatorParam>;

export type BccCondition = Condition<RuleConditionTypesEnum.MESSAGE_BCC, RecipientComparatorParam>;

export type AnyRecipientCondition = Condition<
  RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT,
  RecipientComparatorParam
>;
export type SubjectCondition = Condition<RuleConditionTypesEnum.SUBJECT, RecipientComparatorParam>;

export type MessageBodyCondition = Condition<RuleConditionTypesEnum.MESSAGE_BODY, RecipientComparatorParam>;
export type MessageHtmlBodyCondition = Condition<
  RuleConditionTypesEnum.MESSAGE_HTML_BODY,
  RecipientComparatorParam
>;

export type CommentAuthorCondition = Condition<RuleConditionTypesEnum.COMMENT_AUTHOR, TeammateParam>;

export type CommentBodyCondition = Condition<RuleConditionTypesEnum.COMMENT_BODY, RecipientComparatorParam>;

export type MentionCondition = Condition<RuleConditionTypesEnum.MENTIONS, TeammateParam>;

export type CompareExpressionBooleanCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_BOOLEAN,
  CompareExpressionBooleanParam
>;

export type CompareExpressionNumberCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_NUMBER,
  CompareExpressionNumberParam
>;

export type CompareExpressionStringCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING,
  CompareExpressionStringParam
>;

export type CompareExpressionDateTimeCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_DATETIME,
  CompareExpressionDateTimeParam
>;

export type CompareExpressionTeammateCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_TEAMMATE,
  CompareExpressionTeammateParam
>;

export type CompareExpressionListCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_LIST,
  CompareExpressionListParam
>;

export type CompareExpressionOtherCondition = Condition<
  RuleConditionTypesEnum.COMPARE_EXPRESSION_OTHER,
  CompareExpressionOtherParam
>;

export type CompareExpressionCondition =
  | CompareExpressionBooleanCondition
  | CompareExpressionNumberCondition
  | CompareExpressionStringCondition
  | CompareExpressionDateTimeCondition
  | CompareExpressionTeammateCondition
  | CompareExpressionListCondition
  | CompareExpressionOtherCondition;

export type CommentBodyAndAuthorsCondition = Condition<
  RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS,
  CommentBodyAndAuthorsParam
>;

// missing : below any_recipient, until unrepliedAfter (default/manifest.json)

export type CustomAttributeStringCondition = Condition<
  RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_STRING,
  CustomAttributeStringParam
>;

export type CustomAttributeNumberCondition = Condition<
  RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_NUMBER,
  CustomAttributeNumberParam
>;

export type CustomAttributeBooleanCondition = Condition<
  RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_BOOLEAN,
  CustomAttributeBooleanParam
>;

export type CustomAttributeDatetimeCondition = Condition<
  RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_DATETIME,
  CustomAttributeDatetimeParam
>;

export type CustomAttributeTeammateCondition = Condition<
  RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_TEAMMATE,
  CustomAttributeTeammateParam
>;

export type AccountCustomAttributeStringCondition = Condition<
  RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING,
  CustomAttributeStringParam
>;

export type AccountCustomAttributeNumberCondition = Condition<
  RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_NUMBER,
  CustomAttributeNumberParam
>;

export type AccountCustomAttributeBooleanCondition = Condition<
  RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_BOOLEAN,
  CustomAttributeBooleanParam
>;

export type AccountCustomAttributeDatetimeCondition = Condition<
  RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_DATETIME,
  CustomAttributeDatetimeParam
>;

export type AccountCustomAttributeTeammateCondition = Condition<
  RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_TEAMMATE,
  CustomAttributeTeammateParam
>;

export type CustomAttributeCondition =
  | CustomAttributeStringCondition
  | CustomAttributeNumberCondition
  | CustomAttributeBooleanCondition
  | CustomAttributeDatetimeCondition
  | CustomAttributeTeammateCondition;

export type AccountCustomAttributeCondition =
  | AccountCustomAttributeStringCondition
  | AccountCustomAttributeNumberCondition
  | AccountCustomAttributeBooleanCondition
  | AccountCustomAttributeDatetimeCondition
  | AccountCustomAttributeTeammateCondition;

export type UnrepliedAfterCondition = Condition<RuleConditionTypesEnum.UNREPLIED_AFTER, UnrepliedAfterParam>;

export type DelaySinceTriggerCondition = Condition<RuleConditionTypesEnum.DELAY_SINCE_TRIGGER, DelayParam>;

export type UnassignedAfterCondition = Condition<RuleConditionTypesEnum.UNASSIGNED_AFTER, DelayParam>;

export type DuringBusinessHoursCondition = Condition<RuleConditionTypesEnum.DURING_BUSINESS_HOURS>;

export type OutsideBusinessHoursCondition = Condition<RuleConditionTypesEnum.OUTSIDE_BUSINESS_HOURS>;

export type HasConversationMetadata = Condition<RuleConditionTypesEnum.HAS_CONVERSATION_METADATA, FieldParam>;

export type HasNotConversationMetadata = Condition<
  RuleConditionTypesEnum.HAS_NOT_CONVERSATION_METADATA,
  FieldParam
>;

export type DayIsCondition = Condition<RuleConditionTypesEnum.DAY_IS, {days: DaysOfTheWeekParam}>;

export type TimeIsCondition = Condition<RuleConditionTypesEnum.TIME_IS, TimeRangeParam>;

export type DateIsCondition = Condition<RuleConditionTypesEnum.DATE_IS, DateIsParam>;

export type AttachmentExtensionIsCondition = Condition<
  RuleConditionTypesEnum.ATTACHMENT_EXTENSION_IS,
  {extensions: ReadonlyArray<string>}
>;

export type AttachmentNameContainsCondition = Condition<
  RuleConditionTypesEnum.ATTACHMENT_NAME_CONTAINS,
  RecipientComparatorParam
>;

export type RuleCondition =
  | AllIndividualInboxes
  | AllSharedInboxes
  | InboxIs
  | InboxIn
  | InboxBelongsTo
  | ChannelIs
  | ChannelIn
  | AddedTagIs
  | RemovedTagIs
  | HasTag
  | DoesNotHaveTag
  | HasTags
  | HasntTags
  | HasNoTags
  | AssigneeIs
  | IsUnassigned
  | AssigneeChanged
  | AssigneeIn
  | AssigneeNotIn
  | IsArchived
  | IsSnoozed
  | IsEarliestSlaBreach
  | IsOpen
  | SubjectCondition
  | MessageBodyCondition
  | MessageHtmlBodyCondition
  | CommentAuthorCondition
  | CommentBodyCondition
  | MentionCondition
  | FromCondition
  | ToCondition
  | CcCondition
  | ToCcCondition
  | BccCondition
  | AnyRecipientCondition
  | UnrepliedAfterCondition
  | UnassignedAfterCondition
  | DuringBusinessHoursCondition
  | OutsideBusinessHoursCondition
  | CustomAttributeCondition
  | AccountCustomAttributeCondition
  | DelaySinceTriggerCondition
  | AttachmentNameContainsCondition
  | AttachmentExtensionIsCondition

  // Temporary (does not exist yet)
  | HasConversationMetadata
  | HasNotConversationMetadata
  | TimeIsCondition
  | DateIsCondition
  | DayIsCondition
  | CommentBodyAndAuthorsCondition
  | CompareExpressionCondition;

export interface RuleParametersDependencies {
  channels: ReadonlyArray<ChannelModel>;
  inboxes: ReadonlyArray<InboxModel>;
  teammates: ReadonlyArray<TeammateModel>;

  integrations: {
    conditions: ReadonlyArray<any>;
    actions: ReadonlyArray<any>;
  };
}

export type ClientRuleCondition = WithClientId<RuleCondition>;

/**
 * A full set of conditions is modeled as a 2d-array : RuleCondition[][].
 * First dimension are the ANDs
 * Second dimension are the ORs.
 * e.g: (condA OR condB OR condC...) AND (condD OR condE) AND ...
 */
export type ORConditions = ReadonlyArray<ClientRuleCondition>;
export type ANDConditions = ReadonlyArray<ORConditions>;
export type RuleConditionGroups = ReadonlyArray<DropdownGroup<RuleConditionTypesEnum>>;

export interface RuleEditorConditionsGroup {
  id: RuleEditorConditionsGroupsEnum;
  items: ReadonlyArray<RuleConditionTypesEnum>;
}

export enum RuleEditorConditionsGroupsEnum {
  STATUS = 'status',
  INBOX = 'inbox',
  RECIPIENT = 'recipient',
  CONTACT = 'contact',
  TAGS = 'tags',
  CONTENT = 'content',
  MESSAGE = 'message',
  TIME = 'time',
  TEAMMATES = 'Teammates',
  EXPRESSION = 'expression',
  ACCOUNT = 'account',
}

export const ruleEditorConditionsGroups: ReadonlyArray<RuleEditorConditionsGroup> = [
  {
    id: RuleEditorConditionsGroupsEnum.STATUS,
    items: [
      RuleConditionTypesEnum.IS_ARCHIVED,
      RuleConditionTypesEnum.IS_SNOOZED,
      RuleConditionTypesEnum.IS_OPEN,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.INBOX,
    items: [
      RuleConditionTypesEnum.INBOX_IN,
      RuleConditionTypesEnum.CHANNEL_IN,
      RuleConditionTypesEnum.INBOX_BELONGS_TO,
      RuleConditionTypesEnum.ALL_SHARED_INBOXES,
      RuleConditionTypesEnum.ALL_INDIVIDUAL_INBOXES,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.RECIPIENT,
    items: [
      RuleConditionTypesEnum.MESSAGE_FROM,
      RuleConditionTypesEnum.MESSAGE_TO,
      RuleConditionTypesEnum.MESSAGE_CC,
      RuleConditionTypesEnum.MESSAGE_BCC,
      RuleConditionTypesEnum.MESSAGE_TO_CC,
      RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.CONTACT,
    items: [
      RuleConditionTypesEnum.CONTACT_IN_GROUP,
      RuleConditionTypesEnum.CONTACT_NOT_IN_GROUP,
      /**
       * There are 4 different kinds of custom attributes.
       * We only need to expose one (arbitrarily chosen)
       * to then give the user the possibility to modify the custom field type.
       */
      RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_STRING,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.TAGS,
    items: [
      RuleConditionTypesEnum.HAS_TAG_IN,
      RuleConditionTypesEnum.HASNT_TAGS,
      RuleConditionTypesEnum.HAS_NO_TAGS,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.CONTENT,
    items: [
      RuleConditionTypesEnum.SUBJECT,
      RuleConditionTypesEnum.MESSAGE_HEADERS,
      RuleConditionTypesEnum.MESSAGE_BODY,
      RuleConditionTypesEnum.MESSAGE_HTML_BODY,
      RuleConditionTypesEnum.LANGUAGE,
      RuleConditionTypesEnum.HAS_ATTACHMENTS,
      RuleConditionTypesEnum.HAS_NO_ATTACHMENTS,
      RuleConditionTypesEnum.ATTACHMENT_NAME_CONTAINS,
      RuleConditionTypesEnum.ATTACHMENT_EXTENSION_IS,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.MESSAGE,
    items: [RuleConditionTypesEnum.MESSAGE_TYPE, RuleConditionTypesEnum.MESSAGE_DIRECTION],
  },
  {
    id: RuleEditorConditionsGroupsEnum.TIME,
    items: [
      RuleConditionTypesEnum.UNREPLIED_AFTER,
      RuleConditionTypesEnum.UNASSIGNED_AFTER,
      RuleConditionTypesEnum.DELAY_SINCE_TRIGGER,
      RuleConditionTypesEnum.DURING_BUSINESS_HOURS,
      RuleConditionTypesEnum.OUTSIDE_BUSINESS_HOURS,
      RuleConditionTypesEnum.TIME_IS,
      RuleConditionTypesEnum.DATE_IS,
      RuleConditionTypesEnum.DAY_IS,
    ],
  },
  {
    id: RuleEditorConditionsGroupsEnum.TEAMMATES,
    items: [
      RuleConditionTypesEnum.ASSIGNEE_IN,
      RuleConditionTypesEnum.ASSIGNEE_NOT_IN,
      RuleConditionTypesEnum.IS_UNASSIGNED,
    ],
  },
];

export const ruleEditorConditionsAccountGroups: ReadonlyArray<RuleEditorConditionsGroup> = [
  {
    id: RuleEditorConditionsGroupsEnum.ACCOUNT,
    items: [RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING],
  },
];

/*
 * Type guards.
 */

export function isMentionRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<MentionCondition> {
  return ruleCondition.type === RuleConditionTypesEnum.MENTIONS;
}

export function isAssigneeInRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<AssigneeIn> {
  return ruleCondition.type === RuleConditionTypesEnum.ASSIGNEE_IN;
}

export function isAssigneeChangedRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<AssigneeChanged> {
  return ruleCondition.type === RuleConditionTypesEnum.ASSIGNEE_CHANGED;
}

export function isAssigneeChangedOrAssigneeIn(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<AssigneeIn | AssigneeChanged> {
  return isAssigneeChangedRuleCondition(ruleCondition) || isAssigneeInRuleCondition(ruleCondition);
}

export function isAddedTagRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<AddedTagIs> {
  return ruleCondition.type === RuleConditionTypesEnum.ADDED_TAG_IS;
}

export function isRemovedTagRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<RemovedTagIs> {
  return ruleCondition.type === RuleConditionTypesEnum.REMOVED_TAG_IS;
}

export function isCommentBodyAndAuthorsRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<CommentBodyAndAuthorsCondition> {
  return ruleCondition.type === RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS;
}

export function isFromRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<FromCondition> {
  return ruleCondition.type === RuleConditionTypesEnum.MESSAGE_FROM;
}

export function isToRuleCondition(
  ruleCondition: ClientRuleCondition,
): ruleCondition is WithClientId<ToCcCondition> {
  return (
    ruleCondition.type === RuleConditionTypesEnum.MESSAGE_TO_CC ||
    ruleCondition.type === RuleConditionTypesEnum.MESSAGE_TO
  );
}
